import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  CAMPAIGN_EXECUTION_LIMITS,
  type CampaignDto,
  type CampaignPaceRequest,
  type CampaignReviewResponse,
  type WhatsAppConnectionResponse,
} from "@multi-chat/shared";
import {
  getCampaignReview,
  scheduleCampaign,
  sendCampaignNow,
  sendCampaignTest,
} from "../../api/campaigns-api.js";
import { getConnection } from "../../api/whatsapp-api.js";
import { mapCampaignError } from "./campaigns-errors.js";
import { useNotifications } from "../notifications/notification-context.js";
import { useOrganizationPlatformFlags } from "../organizations/use-organization-platform-flags.js";
import { senderDisplayName, usableWhatsAppSenders } from "./campaign-message-presentation.js";
import { campaignHasAudienceSources } from "./campaigns-presentation.js";
import type { CampaignDraftStepId } from "./campaign-draft-workflow.js";
import {
  buildCampaignReviewChecklist,
  formatCampaignLocalScheduleParts,
} from "./campaign-review-presentation.js";
import { CampaignDeliveryTiming } from "./CampaignDeliveryTiming.js";
import { CampaignLaunchConfirmationDialog } from "./CampaignLaunchConfirmationDialog.js";
import { CampaignReviewActions } from "./CampaignReviewActions.js";
import { CampaignReviewChecklist } from "./CampaignReviewChecklist.js";
import { CampaignScheduleDialog } from "./CampaignScheduleDialog.js";
import { CampaignSectionStatusBadge } from "./CampaignSectionStatusBadge.js";
import { CampaignSendTestDialog } from "./CampaignSendTestDialog.js";

const LAUNCH_HINT_ID = "campaign-launch-hint";

type DeliveryMode = "now" | "schedule";
type PaceMode = "AUTO" | "CUSTOM";

function buildPaceRequest(paceMode: PaceMode, customRate: number): CampaignPaceRequest {
  if (paceMode === "CUSTOM") {
    return {
      paceMode: "CUSTOM",
      customMessagesPerSecond: customRate,
    };
  }
  return { paceMode: "AUTO" };
}

export function CampaignReviewStep({
  organizationId,
  campaign,
  canMutate,
  contentDraftDirty = false,
  messageUnlocked = true,
  actionsSlot,
  statusSlot,
  onNavigateToStep,
  onLaunchReadinessChange,
  onCampaignChanged,
}: {
  organizationId: number;
  campaign: CampaignDto;
  canMutate: boolean;
  /** Content's local unsaved flag; display-only, never a launch gate. */
  contentDraftDirty?: boolean;
  messageUnlocked?: boolean;
  actionsSlot?: HTMLElement | null;
  statusSlot?: HTMLElement | null;
  onNavigateToStep?: (step: CampaignDraftStepId) => void;
  onLaunchReadinessChange?: (ready: boolean | null) => void;
  onCampaignChanged?: () => void | Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { pushNotification } = useNotifications();
  const platformFlags = useOrganizationPlatformFlags(organizationId);
  const whatsappLaunchAllowed = platformFlags == null || platformFlags.whatsappEnabled;
  const [review, setReview] = useState<CampaignReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [connection, setConnection] = useState<WhatsAppConnectionResponse | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>("now");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  // Pace controls are hidden for now; launches use AUTO unless a custom pace is exposed again.
  const [paceMode] = useState<PaceMode>("AUTO");
  const [customMessagesPerSecond] = useState<number>(
    CAMPAIGN_EXECUTION_LIMITS.defaultCustomMessagesPerSecond,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<unknown>(null);

  const isDraft = campaign.status === "DRAFT";
  const organizationTimezone = review?.organizationTimezone ?? "UTC";

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    void (async () => {
      try {
        const [nextReview, conn] = await Promise.all([
          getCampaignReview(organizationId, campaign.id, controller.signal),
          getConnection(organizationId, controller.signal).catch(() => null),
        ]);
        if (controller.signal.aborted) return;
        setReview(nextReview);
        setConnection(conn);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setLoadError(caught);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [
    organizationId,
    campaign.id,
    campaign.updatedAt,
    campaign.snapshotStale,
    campaign.messageConfigured,
    campaign.status,
  ]);

  const senders = useMemo(() => usableWhatsAppSenders(connection), [connection]);
  const selectedSender =
    campaign.platformEndpointId != null
      ? (senders.find((item) => item.platformEndpointId === campaign.platformEndpointId) ?? null)
      : null;
  const coverage = review?.coverage ?? [];
  const blockers = review?.blockers ?? [];
  const launchReady = review?.launchReady ?? false;
  const sendTestReady = review?.sendTestReady ?? false;
  const executableCount = review?.executableRecipientCount ?? 0;
  const excludedCount = review?.excludedRecipientCount ?? 0;

  const senderName = selectedSender
    ? senderDisplayName(selectedSender)
    : t("campaigns.review.senderUnknown");
  const senderPhone = selectedSender?.phoneNumber ?? null;
  const templateLabel = campaign.templateSnapshot.name ?? "—";
  const scheduleParts = formatCampaignLocalScheduleParts(scheduleDate, scheduleTime, i18n.language);
  const audienceSelectionEmpty = !campaignHasAudienceSources(campaign.audienceDefinition);
  const scheduleInputsReady =
    deliveryMode === "now" || (scheduleDate.trim().length > 0 && scheduleTime.trim().length > 0);

  const canSubmitLaunch =
    isDraft &&
    canMutate &&
    whatsappLaunchAllowed &&
    launchReady &&
    scheduleInputsReady &&
    !launching &&
    (paceMode === "AUTO" ||
      (Number.isInteger(customMessagesPerSecond) &&
        customMessagesPerSecond >= CAMPAIGN_EXECUTION_LIMITS.minMessagesPerSecond &&
        customMessagesPerSecond <= CAMPAIGN_EXECUTION_LIMITS.maxMessagesPerSecond));

  const canSendTest = isDraft && canMutate && whatsappLaunchAllowed && sendTestReady && !sending;
  const statusReady = review == null ? null : launchReady && whatsappLaunchAllowed;

  useEffect(() => {
    onLaunchReadinessChange?.(statusReady);
  }, [onLaunchReadinessChange, statusReady]);

  useEffect(() => () => onLaunchReadinessChange?.(null), [onLaunchReadinessChange]);

  const checklistRows = buildCampaignReviewChecklist({
    blockers: blockers.map((blocker) => blocker.code),
    coverage,
    platformEndpointSelected: campaign.platformEndpointId != null,
    templateSelected: campaign.whatsappTemplateId != null,
    whatsappLaunchAllowed,
    contentDraftDirty,
    messageUnlocked,
    audienceSelectionEmpty,
  });
  const mappedVariableCount = coverage.filter(
    (entry) => campaign.variableMapping.variables[entry.parameterKey] != null,
  ).length;

  const launchHint = !whatsappLaunchAllowed
    ? t("campaigns.errors.platformNotEnabled")
    : !canSubmitLaunch
    ? blockers.length > 0
      ? t("campaigns.review.checklist.resolveToSend")
      : deliveryMode === "schedule" && !scheduleInputsReady
        ? t("campaigns.review.launch.chooseDateTime")
        : t("campaigns.review.needsAttention")
    : null;

  const sendTest = async (input: {
    sampleRecipientId: string;
    destinationPhone: string;
    defaultCountryIso: string;
  }) => {
    if (!isDraft || !canMutate || !whatsappLaunchAllowed || !sendTestReady || sending) {
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await sendCampaignTest(organizationId, campaign.id, {
        sampleRecipientId: input.sampleRecipientId,
        destinationPhone: input.destinationPhone,
        defaultCountryIso: input.defaultCountryIso,
        clientIdempotencyKey: crypto.randomUUID(),
      });
      pushNotification({ message: t("campaigns.toasts.testSent") });
      setSendTestOpen(false);
    } catch (caught) {
      setSendError(caught);
      pushNotification({
        kind: "error",
        message: mapCampaignError(t, caught, t("campaigns.errors.requestFailed")),
      });
    } finally {
      setSending(false);
    }
  };

  const executeLaunch = async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      const pace = buildPaceRequest(paceMode, customMessagesPerSecond);
      const launchIdempotencyKey = crypto.randomUUID();
      if (deliveryMode === "now") {
        await sendCampaignNow(organizationId, campaign.id, {
          launchIdempotencyKey,
          pace,
        });
        pushNotification({ message: t("campaigns.toasts.sendStarted") });
      } else {
        await scheduleCampaign(organizationId, campaign.id, {
          launchIdempotencyKey,
          localDate: scheduleDate,
          localTime: scheduleTime,
          timezone: organizationTimezone,
          pace,
        });
        pushNotification({ message: t("campaigns.toasts.scheduled") });
      }
      setConfirmOpen(false);
      await onCampaignChanged?.();
    } catch (caught) {
      setLaunchError(caught);
      pushNotification({
        kind: "error",
        message: mapCampaignError(t, caught, t("campaigns.errors.requestFailed")),
      });
    } finally {
      setLaunching(false);
    }
  };

  if (loading && !review) {
    return (
      <div className="grid place-items-center py-16" data-testid="campaign-review-loading">
        <p className="flex items-center gap-2 text-muted" aria-live="polite">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          {t("campaigns.loading")}
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <p
        className="m-0 rounded-md border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error"
        role="alert"
      >
        {mapCampaignError(t, loadError, t("campaigns.errors.requestFailed"))}
      </p>
    );
  }

  const showLaunchControls = isDraft && canMutate;

  return (
    <div className="grid gap-4" data-testid="campaign-review-step">
      {statusSlot && statusReady != null
        ? createPortal(
            <CampaignSectionStatusBadge
              ready={statusReady}
              readyLabel={t("campaigns.detail.statusReady")}
              pendingLabel={t("campaigns.review.statusNeedsAttention")}
              testId="campaign-review-status"
            />,
            statusSlot,
          )
        : null}
      {showLaunchControls && actionsSlot
        ? createPortal(
            <CampaignReviewActions
              deliveryMode={deliveryMode}
              executableCount={executableCount}
              canSubmit={canSubmitLaunch}
              launching={launching}
              canSendTest={canSendTest}
              launchHintId={launchHint ? LAUNCH_HINT_ID : undefined}
              onSubmit={() => setConfirmOpen(true)}
              onSendTest={() => setSendTestOpen(true)}
            />,
            actionsSlot,
          )
        : null}

      <CampaignReviewChecklist
        rows={checklistRows}
        details={{
          executableCount,
          excludedCount,
          senderName,
          senderPhone,
          templateName: templateLabel,
          templateLanguage: campaign.templateSnapshot.language ?? null,
          templateCategory: campaign.templateSnapshot.category ?? null,
          mappedVariableCount,
        }}
        messageUnlocked={messageUnlocked}
        delivery={
          showLaunchControls
            ? {
                mode: deliveryMode,
                schedule: scheduleParts,
                timezone: organizationTimezone,
                onChooseSchedule: () => setScheduleDialogOpen(true),
              }
            : undefined
        }
        credits={
          review
            ? {
                isUnlimited: review.isUnlimited,
                requiredCredits: review.requiredCredits,
                availableCredits: review.availableCredits,
                reservedCredits: review.reservedCredits,
                insufficientCredits: blockers.some(
                  (b) => b.code === "INSUFFICIENT_MESSAGE_CREDITS",
                ),
              }
            : undefined
        }
        onFix={onNavigateToStep}
      />

      {showLaunchControls ? (
        <CampaignDeliveryTiming
          deliveryMode={deliveryMode}
          onDeliveryModeChange={setDeliveryMode}
          scheduleChosen={scheduleParts != null}
          onOpenSchedule={() => setScheduleDialogOpen(true)}
          launchHint={launchHint}
          launchHintId={LAUNCH_HINT_ID}
          launchError={
            launchError
              ? mapCampaignError(t, launchError, t("campaigns.errors.requestFailed"))
              : null
          }
        />
      ) : !canMutate ? (
        <p className="m-0 text-[0.8125rem] text-muted">{t("campaigns.review.readOnly")}</p>
      ) : null}

      <CampaignScheduleDialog
        open={scheduleDialogOpen}
        timezone={organizationTimezone}
        initialDate={scheduleDate}
        initialTime={scheduleTime}
        onOpenChange={setScheduleDialogOpen}
        onSave={({ localDate, localTime }) => {
          setScheduleDate(localDate);
          setScheduleTime(localTime);
          setDeliveryMode("schedule");
        }}
      />

      <CampaignLaunchConfirmationDialog
        open={confirmOpen}
        deliveryMode={deliveryMode}
        executableCount={executableCount}
        excludedCount={excludedCount}
        requiredCredits={review?.requiredCredits ?? executableCount}
        isUnlimited={review?.isUnlimited ?? true}
        senderName={senderName}
        senderPhone={senderPhone}
        templateName={templateLabel}
        schedule={scheduleParts}
        timezone={organizationTimezone}
        launching={launching}
        onOpenChange={setConfirmOpen}
        onConfirm={() => void executeLaunch()}
      />
      <CampaignSendTestDialog
        open={sendTestOpen}
        organizationId={organizationId}
        campaignId={campaign.id}
        sending={sending}
        errorMessage={
          sendError ? mapCampaignError(t, sendError, t("campaigns.errors.requestFailed")) : null
        }
        onOpenChange={setSendTestOpen}
        onSend={(input) => void sendTest(input)}
      />
    </div>
  );
}
